import React, { useState } from 'react';
import { FaHeart, FaShieldAlt } from 'react-icons/fa';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.marrycenter.app';

/* Official-style Google Play colorful triangle logo */
const GooglePlayIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 28" width="34" height="38" aria-hidden="true">
    <path d="M0.4 0.9C0.15 1.18 0 1.59 0 2.08V25.9c0 .49.15.9.4 1.18L.5 27.2 13.18 14.5v-.3L.5.78z" fill="#00D4EA" />
    <path d="M17.3 18.68l-4.12-4.18V14.2l4.12-4.18.1.06 4.87 2.77c1.39.79 1.39 2.08 0 2.87l-4.87 2.77z" fill="#FFD400" />
    <path d="M17.4 18.62L13.18 14.4.4 27.08c.46.49 1.21.55 2.06.06l14.94-8.52z" fill="#FF3B30" />
    <path d="M.4 0.9L17.4 9.38l-4.22 4.22L0.4 0.9z" fill="#00E676" />
  </svg>
);

const Hero = () => {
  const [isOpening, setIsOpening] = useState(false);

  const handleDownload = () => {
    if (isOpening) return;
    setIsOpening(true);
    window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
    setTimeout(() => setIsOpening(false), 2500);
  };

  return (
    <section className="relative pt-24 pb-12 lg:pt-32 lg:pb-32 overflow-hidden bg-transparent">

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Content */}
          <div className="text-center lg:text-left animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 text-pink-300 backdrop-blur-md border border-white/10 shadow-sm transition-transform hover:scale-105 mb-4 lg:mb-8">
              <FaHeart className="animate-pulse text-pink-400" />
              <span className="text-sm font-semibold tracking-wide">#1 Trusted Matrimony App</span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-romantic font-extrabold text-white leading-[1.1] mb-4 lg:mb-8">
              Find Your <br/>
              <span className="relative inline-block mt-2">
                <span className="relative z-10 bg-gradient-to-r from-pink-400 via-rose-400 to-purple-400 bg-clip-text text-transparent">
                  Perfect Match
                </span>
                <div className="absolute -bottom-2 left-0 w-full h-3 bg-pink-500/20 -z-10 rounded-full transform -skew-x-12"></div>
              </span>
            </h1>

            <p className="text-lg lg:text-xl text-white leading-relaxed max-w-2xl mx-auto lg:mx-0 mb-1 lg:mb-8">
              Join thousands of successful couples who found their soulmate through our intelligent matching platform. Your journey to forever starts here.
            </p>

            {/* Download CTA block */}
            <div className="p-5 sm:p-8 rounded-3xl romantic-glass-card max-w-md mx-auto lg:mx-0 mb-6 lg:mb-8">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 flex items-center justify-center lg:justify-start gap-2">
                Download Marry Center App
              </h3>
              <p className="text-sm sm:text-base text-slate-300 mb-4 sm:mb-6 font-medium">
                Get the ultimate matchmaking experience on your device.
              </p>

              {/* Official-style Play Store Badge Button */}
              <button
                onClick={handleDownload}
                disabled={isOpening}
                aria-label="Download MarryCenter on Google Play"
                className="playstore-btn-romantic group w-full sm:w-auto"
              >
                {isOpening ? (
                  <span className="playstore-btn-spinner border-slate-500 border-t-pink-500" />
                ) : (
                  <div className="transform group-hover:scale-110 transition-transform duration-300">
                    <GooglePlayIcon />
                  </div>
                )}
                <span className="playstore-btn-inner text-left">
                  <span className="playstore-btn-sub text-slate-400">
                    {isOpening ? 'Opening…' : 'Google Play Store'}
                  </span>
                  <span className="playstore-btn-main text-white group-hover:text-pink-400 transition-colors">
                    Download
                  </span>
                </span>
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/10 max-w-md mx-auto lg:mx-0">
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-white mb-1">50K+</div>
                <div className="text-sm font-medium text-slate-400">Happy Couples</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-white mb-1">95%</div>
                <div className="text-sm font-medium text-slate-400">Success Rate</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-3xl font-bold text-white mb-1">150+</div>
                <div className="text-sm font-medium text-slate-400">Cities</div>
              </div>
            </div>
          </div>

          {/* Right Content - Hero Image with Glassmorphism */}
          <div className="relative lg:h-[600px] flex items-center justify-center lg:justify-end animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            {/* Decorative background shapes */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-pink-500/10 to-rose-500/10 rounded-full blur-3xl -z-10"></div>
            
            <div className="relative z-10 w-full max-w-md animate-float">
              {/* Main Image Container */}
              <div className="relative rounded-[2.5rem] p-3 bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl overflow-visible">
                <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/5 to-purple-500/5 rounded-[2.5rem] -z-10"></div>
                <img
                  src="/images/landingpic.png"
                  alt="Happy couple"
                  className="rounded-[2rem] w-full h-[400px] object-cover shadow-inner"
                />
                
                {/* Floating Cards */}
                <div className="absolute -left-8 top-12 p-4 rounded-2xl bg-[#0f041b]/90 backdrop-blur-md border border-white/10 shadow-2xl flex items-center gap-4 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <FaShieldAlt className="text-pink-400 text-xl" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">100% Verified</div>
                    <div className="text-xs text-slate-400">Secure Profiles</div>
                  </div>
                </div>

                <div className="absolute -right-8 bottom-12 p-4 rounded-2xl bg-[#0f041b]/90 backdrop-blur-md border border-white/10 shadow-2xl flex items-center gap-4 animate-float" style={{ animationDelay: '1.5s' }}>
                  <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center relative">
                    <FaHeart className="text-rose-400 text-xl absolute animate-ping opacity-50" />
                    <FaHeart className="text-rose-400 text-xl relative z-10" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-white">New Match!</div>
                    <div className="text-xs text-slate-400">98% Compatibility</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;